<template>
	<SimulatorSheet :stepNum="stepNum" :stepName="stepName">
		<div class="simulator__grid simulator__grid--1-2 align-items-center">
			<p class="sheet__subtitle">{{ $t("Tipo de simulação") }}</p>
			<div>
				<BSelector design="split" v-model="values.type" :options="simulationOptions.type.options" />
			</div>
		</div>

		<div class="sheet__divider"></div>

		<div class="simulator__grid simulator__grid--1-2 align-items-center">
			<div class="sheet__controlTitle">{{ $t("Localização") }}</div>
			<div>
				<BFieldBlock :label="$t('Localização da obra')">
					<BSelect v-model="values.location" :options="simulationOptions.location.options" :style="{
						maxWidth: '285px',
					}" />
				</BFieldBlock>
			</div>
		</div>

		<div class="sheet__divider"></div>

		<div class="simulator__grid simulator__grid--1-1-1 u-mb40">
			<p class="sheet__subtitle">{{ $t("Dados geométricos") }}</p>
			<p class="sheet__details"></p>
		</div>

		<template v-if="values.type === 'FACADE'">
			<div class="simulator__grid simulator__grid--1-2 align-items-center">
				<div class="sheet__controlTitle">{{ $t("Orientação do Perfil") }}</div>
				<div>
					<BSelector design="split" v-model="values.profileOrientation"
						:options="simulationOptions.profileOrientation.options" />
				</div>
			</div>

			<div class="sheet__divider"></div>

			<div class="simulator__grid simulator__grid--1-2 align-items-center">
				<div class="sheet__controlTitle">{{ $t("Formato") }}</div>
				<div>
					<BSelector design="split" v-model="values.profileFormat"
						:options="simulationOptions.profileFormat.options" />
				</div>
			</div>

			<div class="sheet__divider"></div>
		</template>

		<template v-if="values.type === 'ROOF'">
			<div class="simulator__grid simulator__grid--1-1-1 align-items-center" v-if="values.type === 'ROOF'">
				<div>
					<div class="sheet__controlTitle mb-20">{{ $t("Ângulo") }}</div>
					<ImageAngle class="sheet__image" />
				</div>
				<div>
					<BFieldBlock :label="$t('Ângulo')">
						<BFieldNumber v-model="values.angle" :min="0" :max="20" :precision="2" :step="0.01" suffix="º"
							:style="{
								maxWidth: '173px',
							}" />
					</BFieldBlock>
				</div>
			</div>

			<div class="sheet__divider"></div>
		</template>

		<div class="u-stack" :style="{
			gap: '40px',
		}">
			<div class="simulator__grid simulator__grid--1-2">
				<div>
					<div class="sheet__controlTitle mb-20">
						{{ $t("Número e dimensão de vãos") }}
					</div>
					<ImageSpans class="sheet__image" :spans="values.spans" />
				</div>

				<div>
					<div class="bulkSpanOperations bulkSpanOperations--create" v-if="!checkedMainSpans.length">
						<BFieldBlock :label="$t('Número de vãos')">
							<BFieldNumber :decimals="false" :step-btns="true" v-model="bulkSpanCreation.total" :min="0"
								:max="20 - values.spans.main.length" :step="1" :precision="0"
								:disabled="values.spans.main.length === 20" />
						</BFieldBlock>

						<BFieldBlock :label="$t('Dimensão de vãos')">
							<BFieldNumber v-model="bulkSpanCreation.size" :min="0.01" suffix="m" :precision="2"
								:disabled="values.spans.main.length === 20" />
						</BFieldBlock>

						<div class="bulkSpanOperations__actions">
							<BBtn @click="handleBulkSpanCreationBtnClick" class="sheet__addSpanBtn" size="m" :disabled="bulkSpanCreation.total < 1 || bulkSpanCreation.size === ''
								">
								Adicionar
								<BIcon name="Add"></BIcon>
							</BBtn>
						</div>
					</div>

					<div class="bulkSpanOperations bulkSpanOperations--edit" v-if="checkedMainSpans.length">
						<BFieldBlock :label="$t('Número de vãos')">
							<BFieldNumber :decimals="false" :step-btns="true" :disabled="true"
								:value="checkedMainSpans.length" :step="1" :precision="0" />
						</BFieldBlock>

						<BFieldBlock :label="$t('Dimensão de vãos')">
							<BFieldNumber v-model="bulkSpanEdition.size" :min="0.01" :step="0.01" :precision="2"
								suffix="m" :placeholder="bulkSpanEditionFieldPlaceholder" />
						</BFieldBlock>

						<div class="bulkSpanOperations__actions">
							<BBtn size="m" color="secondary" shape="circle"
								@click="handleBulkSpanEditionCancelBtnClick">
								<BIcon name="Close" size="20px" />
							</BBtn>
							<BBtn size="m" color="primary" shape="circle" @click="handleBulkSpanEditionSaveBtnClick">
								<BIcon name="Save" size="20px" />
							</BBtn>
						</div>
					</div>
				</div>
			</div>

			<div class="simulator__grid simulator__grid--1">
				<div class="spansTable">
					<div class="spansTable__row" v-if="values.spans.useConsole">
						<SimulatorSpansTableItem :label="`${$t('Consola')} 1`" :spanItem="values.spans.console[0]"
							:canDelete="false" :canCheck="false" :canDrag="false" />
					</div>
					<draggable v-model="values.spans.main" @start="isDragging = true" @end="isDragging = false"
						ghost-class="is-ghost" handle=".spansTable__dragHandle" class="spansTable__row">
						<SimulatorSpansTableItem v-for="(spanItem, i) in values.spans.main" :key="spanItem.id"
							:label="`${$t('Vão')} ${i + 1}`" :spanItem="spanItem"
							:isChecked="checkedMainSpans.includes(spanItem)" :canDelete="values.spans.main.length > 1"
							:canDrag="values.spans.main.length > 1" :canCheck="values.spans.main.length > 1"
							@delete="handleSpanDelete" @check="handleSpanCheckChange" />
					</draggable>

					<div class="spansTable__row" v-if="values.spans.useConsole">
						<SimulatorSpansTableItem :label="`${$t('Consola')} 2`" :spanItem="values.spans.console[1]"
							:canDelete="false" :canCheck="false" :canDrag="false" />
					</div>
				</div>
			</div>

			<div class="simulator__grid simulator__grid--1-2">
				<div></div>
				<div>
					<button v-if="!values.spans.useConsole" class="sheet__bigAddBtn sheet__bigAddBtn--dark addSpansBtn"
						@click="values.spans.useConsole = true">
						{{ $t("Incluir consolas (opcional)") }}
						<BIcon name="Add"></BIcon>
					</button>

					<button v-if="values.spans.useConsole" class="sheet__bigAddBtn sheet__bigAddBtn--dark addSpansBtn"
						@click="values.spans.useConsole = false">
						{{ $t("Remover consolas") }}
						<BIcon name="Subtract"></BIcon>
					</button>
				</div>
			</div>
		</div>

		<div class="sheet__divider"></div>

		<div class="simulator__grid simulator__grid--1-2 align-items-center">
			<div class="sheet__controlTitle">
				{{ $t("Espaçamento entre perfís") }}
			</div>
			<div>
				<BFieldBlock :label="$t('Espaçamento')">
					<BFieldNumber v-model="values.profileSpacing" :min="0.1" suffix="m" :precision="2" :step="0.01"
						:style="{
							maxWidth: '173px',
						}" />
				</BFieldBlock>
			</div>
		</div>

		<div class="sheet__divider"></div>

		<div class="simulator__grid simulator__grid--1-2 align-items-center">
			<div class="sheet__controlTitle">{{ $t("Largura de apoios") }}</div>
			<div>
				<BSelector design="split" v-model="values.supportWidth"
					:options="simulationOptions.supportWidth.options" />
			</div>
		</div>

		<div class="sheet__footer">
			<BBtn :disabled="!isSaveEnabled" size="l" variant="text" @click="handleRestartBtnClick">{{ $t("Reiniciar")
			}}</BBtn>
			<BBtn @click="handleSaveBtnClick" :disabled="!isSaveEnabled" size="l">{{
				$t("Salvar e Continuar")
			}}</BBtn>
		</div>
	</SimulatorSheet>
</template>

<script>
import { mapMutations, mapActions, mapState } from "vuex";
import { getUid, uniq, deepClone, wait, excludeKeys } from "@/utils";

import SimulatorSheet from "./SimulatorSheet.vue";
import SimulatorSpansTableItem from "./SimulatorSpansTableItem.vue";

import ImageAngle from "./Images/ImageAngle.vue";
import ImageSpans from "./Images/ImageSpans/index.vue";

import Draggable from "vuedraggable";

import BSelector from "@/components/base/BSelector.vue";
import BSelect from "@/components/base/BSelect.vue";
import BFieldBlock from "@/components/base/BFieldBlock.vue";
import BCheckbox from "@/components/base/BCheckbox.vue";
import BFieldNumber from "@/components/base/BFieldNumber.vue";
import BBtn from "@/components/base/BBtn.vue";
import BToggle from "@/components/base/BToggle.vue";
import BIcon from "@/components/base/BIcon/index.vue";

import mixinEmitLifecycleEvents from "@/mixins/emitLifecycleEvents";
import mixinObserveChanges from "@/pages/Simulator/mixins/observeChanges";
import { getDefaultSpans } from "../../store/modules/simulations/models/simulationDefaults";

const bulkSpanCreationsimulationDefaults = {
	total: 0,
	size: 1,
};

const bulkSpanEditionsimulationDefaults = {
	size: 1,
};

export default {
	components: {
		BSelector,
		BToggle,
		BFieldBlock,
		BCheckbox,
		BSelect,
		BFieldNumber,
		BBtn,
		BIcon,
		SimulatorSheet,
		Draggable,
		SimulatorSpansTableItem,
		ImageAngle,
		ImageSpans,
	},
	mixins: [mixinEmitLifecycleEvents, mixinObserveChanges],
	props: {
		...SimulatorSheet.props,
	},
	data() {
		return {
			values: null,
			isDragging: false,

			bulkSpanCreation: {
				...bulkSpanCreationsimulationDefaults,
			},

			bulkSpanEdition: {
				...bulkSpanEditionsimulationDefaults,
			},

			checkAllMainSpansCheckboxValue: false,
			checkedMainSpans: [],

			isSaving: false,
		};
	},
	computed: {
		...mapState("simulator", {
			simulationOptions: "simulationOptions",
			storeSimulation: "simulation",
		}),

		storeValues() {
			return this.storeSimulation.values;
		},

		isSaveEnabled() {
			return this.values.spans.main.length && !this.isSaving;
		},

		isSameValueAcrossCheckedMainSpans() {
			return uniq(this.checkedMainSpans.map(({ size }) => size)).length === 1;
		},

		bulkSpanEditionFieldPlaceholder() {
			if (this.checkedMainSpans.length) {
				if (!this.isSameValueAcrossCheckedMainSpans) {
					return "Múltiplos valores";
				}
			}

			return "";
		},
	},
	methods: {
		...mapMutations("simulator", ["setSimulationValues"]),
		...mapActions("simulator", ["save"]),

		handleSaveBtnClick() {
			this.$emit("save", this.values);
		},

		handleRestartBtnClick() {
			this.$emit("restart");
		},

		handleBulkSpanCreationBtnClick() {
			const { size, total } = this.bulkSpanCreation;
			this.values.spans.main = [
				...this.values.spans.main,
				...[...Array(total).keys()].map(() => ({ size, id: getUid() })),
			];

			this.bulkSpanCreation = { ...bulkSpanCreationsimulationDefaults };
		},

		handleSpanDelete(spanItem) {
			this.values.spans.main = this.values.spans.main.filter(
				(v) => v !== spanItem
			);
		},

		handleSpanCheckChange(spanItem, checked) {
			if (checked) {
				this.checkedMainSpans = [...this.checkedMainSpans, spanItem];
			} else {
				this.checkedMainSpans = this.checkedMainSpans.filter(
					(v) => v !== spanItem
				);
			}
		},

		handleBulkSpanEditionCancelBtnClick() {
			this.checkedMainSpans = [];
		},
		handleBulkSpanEditionSaveBtnClick() {
			this.checkedMainSpans.forEach((item) => {
				item.size = this.bulkSpanEdition.size;
			});
		},
	},

	watch: {
		"bulkSpanCreation.size": {
			handler(bulkSpanCreation) {
				console.log("bulkSpanCreation", bulkSpanCreation);
			},
			deep: true,
		},
		"values.spans.useConsole": {
			handler(useConsole) {
				if (!useConsole) {
					this.values.spans.console = [];
				} else {
					this.values.spans.console = [...getDefaultSpans().console];
				}
			},
		},

		"values.spans.main"(mainSpans) {
			this.checkedMainSpans = this.checkedMainSpans.filter((v) =>
				mainSpans.includes(v)
			);
		},
		checkAllMainSpansCheckboxValue(isChecked) {
			this.checkedMainSpans = isChecked ? [...this.values.spans.main] : [];
		},
		checkedMainSpans: {
			handler(checkedMainSpans) {
				if (!checkedMainSpans.length) {
					this.checkAllMainSpansCheckboxValue = false;
				} else {
					if (this.isSameValueAcrossCheckedMainSpans) {
						this.bulkSpanEdition.size = this.checkedMainSpans[0].size;
					} else {
						this.bulkSpanEdition.size = 1;
					}
				}
			},
			deep: true,
		},
	},

	created() {
		this.values = deepClone(this.storeValues);
	},
};
</script>

<style lang="scss">
// .sheet__includeBracketsBtn {
//     border-radius: 8px;
//     display: flex;
//     align-items: center;
//     justify-content: space-between;
//     background: var(--color-neutral-150);
//     padding: 16px;
//     color: var(--color-neutral-600);
//     cursor: pointer;
// }

// .sheet__includeBracketsBtn__text {
//     font-size: var(--font-size-l);
// }</style>
